import api from './apiClient';
import { onboardingService } from './onboardingService';

export interface Project {
  id: number;
  name: string;
  description?: string;
  aspectRatio: string;
  templateId?: string;
  totalDuration?: number;
  sceneCount?: number;
  recordedSceneCount?: number;
  plannedDurationMs?: number;
  recordedDurationMs?: number;
  firstSceneThumbnail?: string;
  scenes?: Scene[]; // Project includes scenes when fetched by ID
  createdAt: string;
  updatedAt: string;
}

export interface Scene {
  id: number;
  projectId: number;
  title: string;
  description?: string;
  videoUrl?: string;
  duration: number;
  sceneOrder: number;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export interface Transition {
  id: number;
  projectId: number;
  fromSceneId: number;
  toSceneId: number;
  type: string;
  duration: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectData {
  userId?: string;
  name: string;
  description?: string;
  aspectRatio: string;
  templateId?: string;
}

export interface CreateSceneData {
  projectId: number;
  title: string;
  description?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  duration: number;
  sceneOrder: number;
  status?: string;
  videoGuid?: string;
  recordedAt?: number;
  micEnabled?: boolean;
  metadata?: any;
  tags?: string[];
  notes?: string;
  analysisStatus?: string;
}

export interface CreateTransitionData {
  projectId: number;
  fromSceneId: number;
  toSceneId: number;
  type: string;
  duration: number;
  sortOrder: number;
}

class ProjectsManager {
  async createProject(data: CreateProjectData, isTemplateProject = false): Promise<Project> {
    console.log('[ProjectsManager] createProject called, isTemplateProject:', isTemplateProject);
    const response = await api.post('/api/projects', data);
    const project = response.data.project || response.data;
    
    if (isTemplateProject) {
      console.log('[ProjectsManager] Marking create_scenes_project onboarding task...');
      onboardingService.markCreateScenesProject().catch((err) => {
        console.error('[ProjectsManager] Failed to mark onboarding task:', err);
      });
    }
    
    return project;
  }

  async getProjects(): Promise<Project[]> {
    // Server extracts userId from JWT token - no need to pass from frontend
    const response = await api.get('/api/projects');
    return response.data.projects || response.data || [];
  }

  async getProject(id: number): Promise<Project> {
    const response = await api.get(`/api/projects/${id}`);
    return response.data;
  }

  async updateProject(id: number, data: Partial<CreateProjectData>): Promise<Project> {
    const response = await api.put(`/api/projects/${id}`, data);
    return response.data.project || response.data;
  }

  async deleteProject(id: number): Promise<void> {
    await api.delete(`/api/projects/${id}`);
  }

  async createScene(data: CreateSceneData): Promise<Scene> {
    const response = await api.post('/api/scenes', data);
    return response.data.scene || response.data;
  }

  async getScenes(projectId: number): Promise<Scene[]> {
    const response = await api.get(`/api/scenes?projectId=${projectId}`);
    return response.data;
  }

  async updateScene(id: number, data: Partial<CreateSceneData>): Promise<Scene> {
    const response = await api.put(`/api/scenes/${id}`, data);
    const scene = response.data.scene || response.data;
    
    if (data.videoUrl) {
      onboardingService.markRecordFirstVideo().catch(() => {});
    }
    
    return scene;
  }

  async deleteScene(id: number): Promise<void> {
    await api.delete(`/api/scenes/${id}`);
  }

  async createTransition(data: CreateTransitionData): Promise<Transition> {
    const response = await api.post('/api/transitions', data);
    return response.data.transition || response.data;
  }

  async getTransitions(projectId: number): Promise<Transition[]> {
    const response = await api.get(`/api/transitions?projectId=${projectId}`);
    return response.data;
  }

  async updateTransition(id: number, data: Partial<CreateTransitionData>): Promise<Transition> {
    const response = await api.put(`/api/transitions/${id}`, data);
    return response.data.transition || response.data;
  }

  async deleteTransition(id: number): Promise<void> {
    await api.delete(`/api/transitions/${id}`);
  }

  async getUploadUrl(fileName: string, contentType: string): Promise<{ uploadUrl: string; fileUrl: string }> {
    const response = await api.post('/api/objects/upload-url', {
      fileName,
      contentType,
    });
    return response.data;
  }

  async uploadVideoToS3(file: Blob, fileName: string, onProgress?: (progress: number) => void): Promise<string> {
    const { uploadUrl, fileUrl } = await this.getUploadUrl(fileName, file.type);

    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = (e.loaded / e.total) * 100;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(fileUrl);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }

  async uploadAvatarToS3(projectId: number | string, avatarUrl: string): Promise<string> {
    const response = await api.post(`/api/projects/${projectId}/upload-avatar`, { avatarUrl });
    return response.data.s3Key;
  }

  async deleteS3File(fileUrl: string): Promise<void> {
    try {
      const fileName = fileUrl.split('/').pop();
      if (!fileName) {
        throw new Error('Invalid file URL');
      }

      await api.delete(`/api/objects/${encodeURIComponent(fileUrl)}`);
    } catch (error) {
      console.error('[ProjectsManager] Failed to delete S3 file:', error);
      throw error;
    }
  }
}

export const projectsManager = new ProjectsManager();
